import { BookOpenCheck, CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useRef } from "react";

import type {
  CalendarDay,
  CalendarResponse,
  CalendarSession,
  PlanningDateChoice,
} from "@learn-flow/contracts";

import {
  formatDayName,
  formatDayNumber,
  formatFullDate,
  formatMinutes,
  formatRangeLabel,
} from "../calendar/calendar-date.js";
import { calendarDayAccessibleName } from "./schedule-week-picker-label.js";

type CompletionReflectionWeekPlannerProps = {
  calendar: CalendarResponse | undefined;
  choice: PlanningDateChoice;
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
  scheduledDate: string;
  scheduledTime: string;
  today: string;
  onAdd: () => void;
  onCancel: () => void;
  onChangeDate: (date: string) => void;
  onChangeTime: (time: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onRetry: () => void;
};

export function CompletionReflectionWeekPlanner({
  calendar,
  choice,
  isError,
  isFetching,
  isPending,
  scheduledDate,
  scheduledTime,
  today,
  onAdd,
  onCancel,
  onChangeDate,
  onChangeTime,
  onNext,
  onPrevious,
  onRetry,
}: CompletionReflectionWeekPlannerProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const custom = choice === "custom";
  const validDate = Boolean(scheduledDate) && scheduledDate >= today;

  useEffect(() => {
    const selected = scrollRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (typeof selected?.scrollIntoView === "function") {
      selected.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [calendar, scheduledDate]);

  return (
    <section
      className="reflection-week-planner"
      aria-busy={isPending || isFetching}
      aria-labelledby="reflection-week-planner-heading"
    >
      <div className="reflection-week-planner-header">
        <div>
          <strong id="reflection-week-planner-heading">
            {custom ? "Choose a day that works" : "Check the suggested day"}
          </strong>
          <span>
            {custom
              ? "Your current seven-day calendar starts today."
              : "Your suggested date is highlighted. Choose another day if it fits better."}
          </span>
        </div>
        <button
          type="button"
          className="reflection-week-planner-close"
          aria-label="Close session calendar"
          onClick={onCancel}
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      {isPending ? (
        <div className="reflection-week-planner-state" role="status">
          <CalendarDays size={20} aria-hidden="true" />
          <span>Loading your seven-day calendar…</span>
        </div>
      ) : isError || !calendar ? (
        <div className="reflection-week-planner-error" role="alert">
          <div>
            <strong>Your calendar could not be loaded.</strong>
            <span>Choose a date below, or retry without losing this session.</span>
          </div>
          <button type="button" className="soft-button" onClick={onRetry}>
            Retry calendar
          </button>
        </div>
      ) : (
        <>
          <div className="reflection-week-planner-range">
            <button
              type="button"
              className="icon-button bordered"
              aria-label="Previous seven days"
              disabled={calendar.range.startDate <= today}
              onClick={onPrevious}
            >
              <ChevronLeft size={17} aria-hidden="true" />
            </button>
            <strong>{formatRangeLabel(calendar.range.startDate, calendar.range.endDate)}</strong>
            <button
              type="button"
              className="icon-button bordered"
              aria-label="Next seven days"
              onClick={onNext}
            >
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          </div>
          <div
            ref={scrollRef}
            className="reflection-week-planner-scroll"
            role="region"
            aria-label="Selectable seven-day session calendar; scroll horizontally to see every day"
            tabIndex={0}
          >
            <div className="reflection-week-planner-days">
              {calendar.days.map((day) => (
                <ReflectionCalendarDay
                  key={day.date}
                  day={day}
                  current={day.date === calendar.range.currentDate}
                  selected={day.date === scheduledDate}
                  onSelect={() => onChangeDate(day.date)}
                />
              ))}
            </div>
          </div>
          <span className="reflection-week-planner-swipe">
            Swipe sideways to see all seven days.
          </span>
          {isFetching ? (
            <span className="reflection-week-planner-refresh" role="status">
              Refreshing calendar…
            </span>
          ) : null}
        </>
      )}

      <div className="reflection-week-planner-selection">
        <div className="reflection-week-planner-selection-copy" aria-live="polite">
          <strong>
            {scheduledDate ? `${formatFullDate(scheduledDate)} selected` : "No day selected yet"}
          </strong>
          <span>
            {scheduledDate
              ? choice === "one_week" || choice === "two_weeks"
                ? "The suggested date is the third day in its opening view."
                : "Choose another calendar day if it fits better."
              : "Choose any available day to add a session."}
          </span>
        </div>
        {isError || (!isPending && !calendar) ? (
          <label className="reflection-week-planner-field">
            <span>Date</span>
            <input
              type="date"
              min={today}
              value={scheduledDate}
              onChange={(event) => onChangeDate(event.target.value)}
            />
          </label>
        ) : null}
        <label className="reflection-week-planner-field">
          <span>
            Time <i>Optional</i>
          </span>
          <input
            type="time"
            value={scheduledTime}
            onChange={(event) => onChangeTime(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="primary-button"
          disabled={!validDate || isPending}
          onClick={onAdd}
        >
          Add session
        </button>
      </div>
    </section>
  );
}

function ReflectionCalendarDay({
  day,
  current,
  selected,
  onSelect,
}: {
  day: CalendarDay;
  current: boolean;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={`reflection-week-planner-day ${current ? "is-today" : ""} ${
        selected ? "is-selected" : ""
      }`}
      data-date={day.date}
      aria-label={calendarDayAccessibleName(day)}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="reflection-week-planner-day-heading">
        <span>{formatDayName(day.date)}</span>
        <strong>{formatDayNumber(day.date)}</strong>
        {current ? <i>Today</i> : null}
      </span>
      <span className="reflection-week-planner-day-body">
        {day.sessions.length > 0 ? (
          day.sessions.map((session) => (
            <ReflectionCalendarEvent key={session.id} session={session} />
          ))
        ) : (
          <span className="reflection-week-planner-open">
            <strong>Open day</strong>
            <small>No activity planned</small>
          </span>
        )}
      </span>
      <span className="reflection-week-planner-load">
        {day.totalActivityDurationMinutes > 0
          ? `${formatMinutes(day.totalActivityDurationMinutes)} activity time`
          : "No activity time"}
      </span>
    </button>
  );
}

function ReflectionCalendarEvent({ session }: { session: CalendarSession }): React.JSX.Element {
  return (
    <span className="reflection-week-planner-event">
      <span>
        <BookOpenCheck size={9} aria-hidden="true" />
        Session · {session.scheduledTime ?? "Any time"}
      </span>
      <strong>{session.activityTitle}</strong>
    </span>
  );
}
